<template>
  <TitleWrapper
    ref="titleWrapper"
    :filter-option="false"
    title="SUPPORT_QUERY"
    tooltip-title="SUPPORT_QUERY_TYPE"
    :display-breadcrumb="true"
  ></TitleWrapper>
  <DetailCard ref="detailCard" :details="queryDetials" class="my-5" />
  <div
    class="chat-box border p-4 bg-primary-gray-50 flex flex-col rounded-lg"
    :style="chatBoxStyle"
  >
    <div ref="scrollContainer" class="flex-1 overflow-y-auto space-y-4">
      <div
        v-for="(message, index) in conversationList"
        :key="index"
        class="flex my-2 gap-1"
        :class="
          message.inserted_by_id === currentUser.id ? 'justify-end mx-2 ' : 'justify-start mr-4'
        "
      >
        <div v-if="message.inserted_by_id !== currentUser.id">
          <div v-if="message?.inserted_by?.image" class="w-8.5 h-8.5">
            <img :src="message?.inserted_by?.image" class="h-full w-full rounded-full" />
          </div>
          <span
            v-else
            class="w-8.5 h-8.5 rounded-full flex justify-center items-center font-roboto font-normal text-sm leading-6 bg-primary-gray-200 text-primary-gray-600"
          >
            {{ initials(`${message?.inserted_by?.first_name} ${message?.inserted_by?.last_name}`) }}
          </span>
        </div>
        <div
          class="px-4 py-2 rounded-lg max-w-full font-inter font-medium text-sm text-primary-gray-900 break-words"
          :class="
            message.inserted_by_id === currentUser.id
              ? 'bg-primary-purple-100 text-right '
              : 'bg-primary-gray-100 text-left'
          "
        >
          <div v-if="message.inserted_by_id !== currentUser.id" class="text-primary-purple-700">
            {{ message?.full_name || 'Jhon Smith' }}
          </div>
          <div>
            {{ message.message }}
          </div>
        </div>
      </div>
    </div>
    <div class="mt-4 flex items-center gap-2">
      <UiInput
        v-model="newMessage"
        type="text"
        name="message"
        placeholder="TYPE_YOUR_MESSAGE"
        class="-mb-5"
        :disabled="isQueryClosed"
        @keydown.enter="sendMessage"
      />
      <UIButton button="primary" :disabled="isQueryClosed" @click="sendMessage">Send</UIButton>
    </div>
  </div>
  <AddParticipantsModal
    v-if="isModalShow"
    :modal="isModalShow"
    :student-id="studentId"
    :modal-action="modalAction"
    @toggle="toggleModal"
  />
  <UIConfirmationModal
    v-if="toggleReOpenSupportQueryModel"
    :modal="toggleReOpenSupportQueryModel"
    heading="REOPEN_SUPPORT_QUERY"
    message="Are you sure you want to reopen "
    button-text="Reopen"
    remaining-message="Support Query?"
    :name="currentSupportqueryTitle"
    @cancel="toggleReOpenSupportQueryModelShow"
    @confirm="reOpenCurrentSupportQuery"
  />
</template>

<script>
import TitleWrapper from '@/src/components/TitleWrapper.vue'
import { mapActions, mapState } from 'vuex'
import { buildWhereQuery } from '@/src/utils/filters'
import FILTER_KEYS from '@src/constants/filter-constants.js'
import generalMixins from '@src/mixins/general-mixins.js'
import UIButton from '@src/components/UiElements/UIButton.vue'
import UiInput from '@components/UiElements/UiInputBox.vue'
import GENERAL_CONSTANTS from '@src/constants/general-constants.js'
import AddParticipantsModal from '@src/router/views/admin/modals/addParticipantsModal.vue'
import UIConfirmationModal from '@src/components/UiElements/UIConfirmationModal.vue'
import DetailCard from '@/src/components/UiElements/DetailCard.vue'
import DATE_TIME_CONSTANT from '@src/constants/date-time-constants.js'

export default {
  components: {
    TitleWrapper,
    UIButton,
    UiInput,
    AddParticipantsModal,
    UIConfirmationModal,
    DetailCard,
  },
  mixins: [generalMixins],

  data() {
    return {
      GENERAL_CONSTANTS,
      DATE_TIME_CONSTANT,
      active: 1,
      messageCount: GENERAL_CONSTANTS.EMPTY_RECORD,
      recordLimit: GENERAL_CONSTANTS.RECORD_LIMIT,
      conversationList: [],
      newMessage: '',
      supportQueryChannel: {},
      isModalShow: false,
      studentId: '',
      modalAction: '',
      lastActivity: '',
      queryDetials: {},
      currentSupportqueryTitle: '',
      chatHeight: 'calc(100vh - 200px)', // Default fallback
      viewportHeight: 0,
      tableHeight: 0,
      detailCardHeight: 0,
      resizeObserver: null,
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state?.auth?.currentUser,
      toggleReOpenSupportQueryModel: (state) => state?.supportQuery?.toggleReOpenSupportQueryModel,
      titleWrapperHeight: (state) => state.layout.titleWrapperHeight,
      headerOffSetHeight: (state) => state.layout.headerOffSetHeight,
    }),
    isQueryClosed() {
      return this.$route?.query?.status?.toLowerCase() === 'closed'
    },
    chatBoxStyle() {
      return this.tableHeight
        ? {}
        : {
            height: `${
              (this.viewportHeight || (typeof window !== 'undefined' ? window.innerHeight : 0)) -
              (this.titleWrapperHeight + this.headerOffSetHeight + this.detailCardHeight + 78)
            }px`,
          }
    },
  },
  mounted() {
    this.onSupportQueryChannel()
    const scrollContainer = this.$refs.scrollContainer
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', this.handleScroll)
      this.$nextTick(() => {
        scrollContainer.scrollTop = scrollContainer.scrollHeight
      })
    }

    this.supportQueryMessages()
    this.setRightbarData()
    this.updateViewportHeight()
    this.updateDetailCardHeight()
    this.setupResizeObserver()
  },

  beforeUnmount() {
    const scrollContainer = this.$refs.scrollContainer
    if (scrollContainer) {
      scrollContainer.removeEventListener('scroll', this.handleScroll)
    }
    // Clean up ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
  },
  methods: {
    updateViewportHeight() {
      this.viewportHeight = window?.innerHeight || GENERAL_CONSTANTS.EMPTY_RECORD
    },

    updateDetailCardHeight() {
      this.$nextTick(() => {
        if (this.$refs.detailCard) {
          this.detailCardHeight = this.$refs.detailCard.$el.offsetHeight
        }
      })
    },

    setupResizeObserver() {
      this.$nextTick(() => {
        if (!this.resizeObserver) {
          this.resizeObserver = new ResizeObserver(() => {
            this.updateViewportHeight()
            this.updateDetailCardHeight()
          })
        }

        // Observe detail card element if available
        if (this.$refs.detailCard && this.$refs.detailCard.$el) {
          this.resizeObserver.observe(this.$refs.detailCard.$el)
        }

        // Also observe document root to react to viewport changes
        if (document && document.documentElement) {
          this.resizeObserver.observe(document.documentElement)
        }
      })
    },

    toggleReOpenSupportQueryModelShow() {
      this.$store.commit('supportQuery/SET_REOPEN_QUERY_MODAL', false)
    },

    toggleModal(modalType) {
      this.modalAction = modalType
      this.isModalShow = !this.isModalShow
    },

    onSupportQueryChannel() {
      if (!this.$apiSocketChannel?.channels?.length) {
        const authToken = localStorage.getItem('jwt') || null
        this.$connectApiSocket('/tenant-api-socket', authToken)
      }

      if (!!this.$apiSocketChannel) {
        this.supportQueryChannel = this.$apiSocketChannel.channel(
          `support_query:${this.$route?.params?.id}`,
        )

        this.supportQueryChannel
          .join()
          .receive('ok', ({ message }) => console.log(message)) // eslint-disable-line no-console
          .receive('error', ({ reason }) => console.log(reason)) // eslint-disable-line no-console
          .receive('timeout', () => console.log('timeout')) // eslint-disable-line no-console

        this.supportQueryChannel.on('support_query_outgoing', (response) => {
          response.full_name = `${response?.inserted_by?.first_name} ${response?.inserted_by?.last_name}`
          this.messageCount++
          this.setRightbarData(this.messageCount)
          this.conversationList.push(response)
          // Scroll to bottom when receiving new messages
          this.scrollToBottom()
        })
      }
    },
    async reOpenCurrentSupportQuery() {
      await this.reOpenSupportQuery(this.$route?.params?.id)
      this.toggleReOpenSupportQueryModelShow()
      this.supportQueryMessages()
    },

    handleScroll() {
      const scrollContainer = this.$refs.scrollContainer
      if (scrollContainer.scrollTop === 0) {
        let maxRange = this.paginationCounts(this.messageCount, this.recordLimit)

        if (this.active < maxRange) {
          this.active++
          const range = {
            skip: (this.active - 1) * this.recordLimit,
            limit: this.recordLimit,
          }
          this.supportQueryMessages(range)
        }
      }
    },

    async supportQueryMessages(range) {
      let newMessages = []
      let payload = {
        ...range,
        $where: {
          ...buildWhereQuery(FILTER_KEYS.EQUAL, 'support_query_id', this.$route?.params?.id),
        },
      }
      const [res, err] = await this.getSupportQueryMessages(payload)
      if (!err) {
        newMessages = this.transformMessagesResponse(res)

        if (range?.skip) {
          const scrollContainer = this.$refs.scrollContainer
          const previousScrollHeight = scrollContainer.scrollHeight
          this.conversationList = [...newMessages, ...this.conversationList]
          this.$nextTick(() => {
            scrollContainer.scrollTop = scrollContainer.scrollHeight - previousScrollHeight
          })
        } else {
          this.conversationList = newMessages
          this.$nextTick(() => {
            const scrollContainer = this.$refs.scrollContainer
            scrollContainer.scrollTop = scrollContainer.scrollHeight
          })
        }
        this.messageCount = res?.data.meta?.total_records
        this.setRightbarData(this.messageCount)
      }
    },
    transformMessagesResponse(res) {
      console.log(res, 'res')
      const records = res?.data?.records || []
      const reversed = [...records].reverse()
      const first = records[0] || {}
      const supportQuery = first?.support_query || {}
      const participants = supportQuery?.support_query_participants || []
      const teacher = participants.find((p) => p.role === 'section_teacher') || {}
      const guardian = participants.find((p) => p.role === 'guardian') || {}

      this.studentId = supportQuery?.student?.id || ''
      this.lastActivity = supportQuery?.closed_at || ''
      this.currentSupportqueryTitle = supportQuery?.title || ''

      const lastIndex = records.length - 1
      this.queryDetials = {
        title: supportQuery?.title,
        student: supportQuery?.student?.full_name,
        teacher: teacher?.user?.full_name,
        guardian: guardian?.user?.full_name,
        QueryInitiatedAt: this.$filters.formattedDate(
          supportQuery?.inserted_at,
          DATE_TIME_CONSTANT.DATE_TIME_12,
        ),
        LastActivity: this.$filters.formattedDate(
          records[lastIndex]?.inserted_at,
          DATE_TIME_CONSTANT.DATE_TIME_12,
        ),
        status: this.$route?.query?.status,
      }

      return reversed
    },
    sendMessage() {
      if (!this.newMessage) return
      const payload = {
        message: this.newMessage,
      }

      this.supportQueryChannel
        .push('support_query_incoming', payload)
        .receive('ok', (response) => {
          if (response?.error) {
            this.$store.commit(
              'toast/NEW',
              { type: 'error', message: 'Support query is closed' },
              { root: true },
            )
          }
          // Scroll to bottom after successful message send
          this.scrollToBottom()
        })
        .receive('error', (error) => {
          console.error('Failed to send message:', error)
        })
      this.newMessage = ''
    },

    scrollToBottom() {
      this.$nextTick(() => {
        setTimeout(() => {
          const scrollContainer = this.$refs.scrollContainer
          if (scrollContainer) {
            scrollContainer.scrollTop = scrollContainer.scrollHeight
          }
        }, 100)
      })
    },

    setRightbarData(length = GENERAL_CONSTANTS.EMPTY_RECORD) {
      const rightBarContent = {
        header: {
          title: 'SUPPORT_QUERY_CONVERSATION',

          buttons: this.isQueryClosed
            ? [
                {
                  title: 'REOPEN',
                  classes: ['primary-button-right'],
                  action: {
                    func: () => {
                      this.$store.commit('supportQuery/SET_REOPEN_QUERY_MODAL', true)
                    },
                  },
                },
              ]
            : [
                {
                  title: 'ADD_PARTICIPANTS',
                  classes: ['primary-button-right'],
                  action: {
                    func: () => {
                      this.toggleModal('add participants')
                    },
                  },
                },
                {
                  title: 'ADD_WATCHER',
                  classes: ['primary-button-right'],
                  action: {
                    func: () => {
                      this.toggleModal('add watcher')
                    },
                  },
                },
              ],
        },
        stats: [
          {
            title: GENERAL_CONSTANTS.STATS_AND_SUMMARY,
            categories: [{ text: 'TOTAL_MESSAGES', value: length }],
          },
        ],
      }
      this.setRightbarContent(rightBarContent)
    },
    ...mapActions('layout', ['setRightbarContent']),
    ...mapActions('supportQuery', [
      'getSupportQueryMessages',
      'sendConversationMessages',
      'reOpenSupportQuery',
    ]),
  },
}
</script>
